import * as React from 'react';

import {
  GenericPropertiesEditor,
  GenericPropertiesEditorProps,
} from 'bones/components/molecules/GenericPropertiesEditor/GenericPropertiesEditor';
import { GenericProperty } from 'bones/components/molecules/GenericPropertiesEditor/types';

export interface ManagedGenericPropertiesEditorProps
  extends Omit<
    GenericPropertiesEditorProps,
    'onAddProperty' | 'onPropertyChange' | 'onRemoveProperty'
  > {
  onPropertiesUpdated?: (properties: GenericProperty[]) => void;
}

export const ManagedGenericPropertiesEditor: React.VFC<
  ManagedGenericPropertiesEditorProps
> = ({ properties, onPropertiesUpdated, ...otherProps }) => {
  const [localProperties, setLocalProperties] =
    React.useState<GenericProperty[]>(properties);

  const setProperties = (properties: GenericProperty[]) => {
    setLocalProperties(properties);
    onPropertiesUpdated?.(properties);
  };

  const propertiesByKey = React.useMemo<Record<string, GenericProperty>>(
    () =>
      localProperties.reduce(
        (agg, { key, name, value }) => ({
          ...agg,
          [key]: { key, name, value },
        }),
        {}
      ),
    [localProperties]
  );

  return (
    <GenericPropertiesEditor
      onAddProperty={(property) =>
        setProperties(
          Object.values({
            ...propertiesByKey,
            [property.key]: property,
          })
        )
      }
      onPropertyChange={(property) =>
        setProperties(
          Object.values({
            ...propertiesByKey,
            [property.key]: property,
          })
        )
      }
      onRemoveProperty={({ key: propertyKey }) => {
        setProperties(localProperties.filter(({ key }) => key !== propertyKey));
      }}
      properties={localProperties}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...otherProps}
    />
  );
};
